
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import OpeningBalanceModal from './components/OpeningBalanceModal';
import OutletCheckoutModal from './components/OutletCheckoutModal';
import VerifySessionModal from './components/VerifySessionModal';
import { posApi } from '../../api/pos';
import { unwrapResponse } from '../../api/client';
import { useAuth } from '../../auth/AuthContext';
import { toast } from '../../components/toast';
import { appendDrawerEvent } from '../../utils/drawerLedger';

interface Outlet {
  id: string;
  name: string;
  code: string;
  address: string;
  warehouseId?: number;
  warehouse?: {
    id?: number;
    name?: string;
  };
  isOccupiedByOtherUser?: boolean;
  accountBalance?: number;
  cashAmount?: number;
  lastSessionInfo?: any;
}

interface Counter {
  id: string;
  name: string;
  status: 'available' | 'occupied' | 'maintenance';
  isActive?: boolean;
  lastSessionInfo?: any;
  userEmail?: string;
  hasActiveSession?: boolean;
  isActiveForCurrentUser?: boolean;
}

export default function POSCheckIn() {
  const navigate = useNavigate();
  const { signOut, user } = useAuth();
  const [selectedOutlet, setSelectedOutlet] = useState<string>('');
  const [selectedCounter, setSelectedCounter] = useState<string>('');
  const [selectedCounterData, setSelectedCounterData] = useState<any>(null);
  const [showOpeningBalance, setShowOpeningBalance] = useState(false);
  const [verifiedData, setVerifiedData] = useState<any>(null);
  const [availableCounters, setAvailableCounters] = useState<Counter[]>([]);
  const [outlets, setOutlets] = useState<Outlet[]>([]);
  const [loadingOutlets, setLoadingOutlets] = useState(false);
  const [loadingCounters, setLoadingCounters] = useState(false);
  const [loadingOutletSession, setLoadingOutletSession] = useState(false);
  const [outletSessionInfo, setOutletSessionInfo] = useState<any>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const [shiftError, setShiftError] = useState<string | null>(null);
  const [verifyingCounters, setVerifyingCounters] = useState<Record<string, boolean>>({});
  const [verifiedCounters, setVerifiedCounters] = useState<Set<string>>(new Set());
  const [checkingInOutlet, setCheckingInOutlet] = useState(false);
  const [outletActionError, setOutletActionError] = useState<string | null>(null);
  const [showOutletCheckoutModal, setShowOutletCheckoutModal] = useState(false);
  const [checkoutModalSession, setCheckoutModalSession] = useState<any>(null);
  const [verifyModalCounter, setVerifyModalCounter] = useState<Counter | null>(null);
  const [checkedInOutletId, setCheckedInOutletId] = useState<string | null>(null);
  const hasAutoSelectedRef = useRef(false);
  const selectedOutletRef = useRef<string>('');


  const mapOutlet = (outlet: any): Outlet => ({
    id: String(outlet?.id ?? outlet?.outletId ?? outlet?.OutletId ?? outlet?.code ?? outlet?.Code ?? ""),
    name: outlet?.name ?? outlet?.outletName ?? outlet?.Name ?? "Outlet",
    code: outlet?.code ?? outlet?.Code ?? "",
    address: outlet?.address ?? outlet?.Address ?? "",
    warehouseId: Number(outlet?.warehouse?.id ?? outlet?.warehouseId ?? 0) || undefined,
    warehouse: outlet?.warehouse
      ? {
          id: Number(outlet?.warehouse?.id ?? 0) || undefined,
          name: outlet?.warehouse?.name,
        }
      : undefined,
  });

  const mapCounter = (counter: any): Counter => {
    const currentUserIdRaw = localStorage.getItem("userId");
    const currentUserId = currentUserIdRaw ? Number(currentUserIdRaw) : null;
    const rawStatus = String(counter?.status ?? "").toLowerCase();
    const isActive = counter?.isActive !== false;
    const hasActiveSession =
      counter?.lastSessionInfo &&
      counter?.lastSessionInfo?.endTime == null;
    const isActiveForCurrentUser =
      hasActiveSession &&
      currentUserId != null &&
      Number(counter?.lastSessionInfo?.user?.id) === currentUserId;
    const status: Counter["status"] =
      rawStatus === "available" || rawStatus === "occupied" || rawStatus === "maintenance"
        ? (rawStatus as Counter["status"])
        : !isActive
        ? "maintenance"
        : hasActiveSession && !isActiveForCurrentUser
        ? "occupied"
        : "available";

    return {
      id: String(counter?.id ?? counter?.counterId ?? counter?.CounterId ?? counter?.code ?? counter?.Code ?? ""),
      name: counter?.name ?? counter?.counterName ?? counter?.Name ?? "Counter",
      status,
      isActive: !!counter?.isActive,
      lastSessionInfo: counter?.lastSessionInfo ?? null,
      userEmail: counter?.lastSessionInfo?.user?.email ?? counter?.userEmail ?? "",
      hasActiveSession,
      isActiveForCurrentUser,
    };
  };

  const formatMoney = (value?: number | null) => {
    const amount = typeof value === "number" ? value : 0;
    return `${amount.toFixed(2)}`;
  };

  const getCurrentUserId = () => {
    const clientProfileRaw = localStorage.getItem("clientBusinessProfile");
    if (!clientProfileRaw) return null;
    try {
      const profile = JSON.parse(clientProfileRaw);
      return profile?.userId ?? profile?.UserId ?? null;
    } catch {
      return null;
    }
  };

  const loadOutlets = async () => {
    setLoadingOutlets(true);
    setApiError(null);
    try {
      const response = await posApi.getOutlets({ isActive: true });
      const data = unwrapResponse<any[]>(response);
      if (Array.isArray(data) && data.length > 0) {
        const currentUserId = Number(localStorage.getItem("userId") ?? "");

        const mappedOutlets = data.map((o: any) => {
          const mapped = mapOutlet(o);
          const sessionUserId = Number(o.lastSessionInfo?.user?.id ?? 0);
          const sessionActive = o.lastSessionInfo?.endTime == null;
          mapped.isOccupiedByOtherUser =
            sessionActive && !!sessionUserId && sessionUserId !== currentUserId;
          mapped.accountBalance = typeof o.accountBalance === 'number' ? o.accountBalance : 0;
          mapped.cashAmount = typeof o.cashAmount === 'number' ? o.cashAmount : (typeof o.accountBalance === 'number' ? o.accountBalance : 0);
          mapped.lastSessionInfo = o.lastSessionInfo ?? null;
          return mapped;
        });
        setOutlets(mappedOutlets);

        // Detect if current user has an active outlet session from API
        if (currentUserId) {
          const activeOutlet = data.find(
            (o: any) =>
              o.lastSessionInfo?.endTime == null &&
              Number(o.lastSessionInfo?.user?.id) === currentUserId
          );
          if (activeOutlet) {
            setCheckedInOutletId(String(activeOutlet.id));
          }
        }
      } else {
        setOutlets([]);
      }
    } catch (err) {
      setApiError("Failed to load outlets from backend.");
      setOutlets([]);
    } finally {
      setLoadingOutlets(false);
    }
  };

  // Load outlets on mount and whenever the user returns to this tab/window
  useEffect(() => {
    loadOutlets();

    const refetch = async () => {
      await loadOutlets();
      const outletId = selectedOutletRef.current;
      if (outletId) {
        const freshSession = await loadOutletSession(outletId);
        await loadCounters(outletId, freshSession);
      }
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') refetch();
    };
    const handleFocus = () => refetch();

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When outlets finish loading, auto-select the checked-in outlet so the
  // "Select Counter" section appears without requiring another click.
  useEffect(() => {
    if (hasAutoSelectedRef.current || !checkedInOutletId || !outlets.length || selectedOutlet) return;
    if (!outlets.find((o) => o.id === checkedInOutletId)) return;
    hasAutoSelectedRef.current = true;
    handleOutletSelect(checkedInOutletId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outlets, checkedInOutletId]);

  const loadCounters = async (outletId: string, outletSession?: any) => {
    setLoadingCounters(true);
    try {
      const response = await posApi.getCounters({
        outletId: Number(outletId),
        isActive: null,
      });
      const data = unwrapResponse<any[]>(response);
      if (Array.isArray(data)) {
        const isSessionActive = outletSession?.endTime == null;
        const mapped = data.map((counter) => {
          const mappedCounter = mapCounter(counter);
          if (!isSessionActive) {
            return {
              ...mappedCounter,
              hasActiveSession: false,
              isActiveForCurrentUser: false,
              status: mappedCounter.isActive ? "available" as const : "maintenance" as const,
            };
          }
          return mappedCounter;
        });
        setAvailableCounters(mapped);
        if (selectedCounter) {
          const refreshed = mapped.find((item) => item.id === selectedCounter);
          setSelectedCounterData(refreshed ?? null);
        }
        return mapped;
      } else {
        setAvailableCounters([]);
        return [];
      }
    } catch {
      setAvailableCounters([]);
      return [];
    } finally {
      setLoadingCounters(false);
    }
  };

  const loadOutletSession = async (outletId: string) => {
    setLoadingOutletSession(true);
    try {
      const sessionRes = await posApi.getLastOutletSession(Number(outletId));
      const sessionData = unwrapResponse<any>(sessionRes);
      setOutletSessionInfo(sessionData);
      localStorage.setItem("outletSessionInfo", JSON.stringify(sessionData));
      return sessionData;
    } catch {
      setOutletSessionInfo(null);
      return null;
    } finally {
      setLoadingOutletSession(false);
    }
  };

  // Handle outlet selection
  const handleOutletSelect = async (outletId: string) => {
    setSelectedOutlet(outletId);
    selectedOutletRef.current = outletId;
    setSelectedCounter('');
    setSelectedCounterData(null);
    setOutletActionError(null);
    setAvailableCounters([]);
    setOutletSessionInfo(null);
    const selectedOutletData = outlets.find((outlet) => outlet.id === outletId);
    if (selectedOutletData) {
      localStorage.setItem("outlet", JSON.stringify(selectedOutletData));
      const outletWarehouseId =
        Number(
          selectedOutletData?.warehouse?.id ?? selectedOutletData?.warehouseId ?? 0
        ) || 0;
      if (outletWarehouseId) {
        localStorage.setItem("warehouseId", String(outletWarehouseId));
      } else {
        localStorage.removeItem("warehouseId");
      }
    }

    let sessionData = await loadOutletSession(outletId);
    const isActive = sessionData && sessionData?.endTime == null;
    let checkInFailed = false;

    if (!isActive) {
      setCheckingInOutlet(true);
      try {
        const userId = getCurrentUserId();
        const now = new Date();
        const tzOffset = -now.getTimezoneOffset();
        const sign = tzOffset >= 0 ? '+' : '-';
        const pad = (n: number) => String(Math.floor(Math.abs(n))).padStart(2, '0');
        const localISOTime = `${now.getFullYear()}-${pad(now.getMonth()+1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}${sign}${pad(tzOffset/60)}:${pad(tzOffset%60)}`;
        const checkInPayload = {
          outletId: Number(outletId),
          userId,
          openingCash: outlets.find(o => o.id === outletId)?.accountBalance ?? 0,
          startTime: localISOTime,
        };
        const checkInRes = await posApi.checkInOutlet(checkInPayload) as any;
        if (checkInRes?.succeeded === false) {
          const errorMsg = checkInRes?.errors?.[0] ?? checkInRes?.message ?? "Failed to check in outlet.";
          toast.error(errorMsg);
          setSelectedOutlet('');
          selectedOutletRef.current = '';
          checkInFailed = true;
        } else {
          const latestSession = await loadOutletSession(outletId);
          sessionData = latestSession;
        }
      } catch {
        toast.error("Failed to check in outlet. Please try again.");
        setSelectedOutlet('');
        selectedOutletRef.current = '';
        checkInFailed = true;
      } finally {
        setCheckingInOutlet(false);
      }
    }

    if (!checkInFailed) {
      const currentUserId = Number(localStorage.getItem("userId") ?? "");
      localStorage.setItem("pendingCheckedInOutlet", JSON.stringify({ outletId, userId: currentUserId }));
      setCheckedInOutletId(outletId);
      await loadCounters(outletId, sessionData);
    } else {
      setAvailableCounters([]);
    }
  };

  const handleStartShift = async () => {
    if (!selectedOutlet || !selectedCounterData) return;

    setShiftError(null);

    // Block check-in if user already has an active session on a different counter
    const activeCounterForUser = availableCounters.find(c => c.isActiveForCurrentUser);
    if (activeCounterForUser && activeCounterForUser.id !== selectedCounterData.id) {
      setShiftError(`You already have an active session on "${activeCounterForUser.name}". Please check out from that counter first.`);
      return;
    }

    // Resume existing session for current user without creating a new one
    if (selectedCounterData?.isActiveForCurrentUser) {
      try {
        const counterLastSessionRes = await posApi.getLastCounterSession(
          Number(selectedCounterData.id)
        );
        const counterLastSession = unwrapResponse<any>(counterLastSessionRes);

        const sessionId = counterLastSession?.id;
        let counterSessionInfo = counterLastSession;
        if (sessionId) {
          const counterSessionRes = await posApi.getCounterSessionInfo(sessionId);
          counterSessionInfo = unwrapResponse<any>(counterSessionRes) ?? counterLastSession;
        }

        const currentOutlet = outlets.find((o) => o.id === selectedOutlet);
        const outletDetailsRes = await posApi.getOutletById(Number(selectedOutlet));
        const outletDetails = unwrapResponse<any>(outletDetailsRes);

        localStorage.setItem(
          "counterCheckedInInfo",
          JSON.stringify({
            outletName: currentOutlet?.name,
            counterName: selectedCounterData?.name,
          })
        );
        localStorage.setItem("counterSession", JSON.stringify(counterSessionInfo));
        localStorage.setItem("counter", JSON.stringify(selectedCounterData));
        localStorage.setItem("outlet", JSON.stringify(currentOutlet));
        localStorage.setItem("outletSession", JSON.stringify(outletSessionInfo));

        const warehouseId =
          Number(
            outletDetails?.warehouse?.id ??
              currentOutlet?.warehouse?.id ??
              currentOutlet?.warehouseId ??
              0
          ) || 0;
        if (warehouseId) {
          localStorage.setItem("warehouseId", String(warehouseId));
        }

        navigate("/pos-checkout");
      } catch {
        setShiftError("Failed to resume session. Please try again.");
      }
      return;
    }

    try {
      if (!outletSessionInfo || outletSessionInfo?.endTime != null) {
        setShiftError("Outlet is not checked in. Please contact your outlet manager.");
        return;
      }

      const outletDetailsRes = await posApi.getOutletById(Number(selectedOutlet));
      const outletDetails = unwrapResponse<any>(outletDetailsRes);

      const counterLastSessionRes = await posApi.getLastCounterSession(
        Number(selectedCounterData.id)
      );
      const counterLastSession = unwrapResponse<any>(counterLastSessionRes);

      const currentOutlet = outlets.find(o => o.id === selectedOutlet);
      const outletAccountBalance = Number(currentOutlet?.cashAmount ?? currentOutlet?.accountBalance ?? 0);

      let openingCash = 0;
      if (
        counterLastSession?.outletSession?.id &&
        outletSessionInfo?.id &&
        counterLastSession.outletSession.id === outletSessionInfo.id
      ) {
        openingCash = counterLastSession?.closingCash ?? outletAccountBalance;
      } else {
        const balanceInfo = outletSessionInfo?.balanceInfo ?? [];
        const balance = balanceInfo.find(
          (info: any) => Number(info?.counterId) === Number(selectedCounterData.id)
        );
        openingCash = Number(balance?.openingBalance ?? 0) || outletAccountBalance;
      }

      let currentUserName = "";
      let currentUserEmail = "";
      try {
        const userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");
        currentUserName = userInfo?.name ?? userInfo?.Name ?? "";
        currentUserEmail = userInfo?.email ?? userInfo?.Email ?? "";
      } catch {
        currentUserName = "";
      }

      const checkInData = {
        outlet: currentOutlet,
        outletDetails,
        counter: selectedCounterData,
        outletSession: outletSessionInfo,
        counterLastSession,
        openingCash,
        user: {
          name: currentUserName,
          email: currentUserEmail,
        },
        timestamp: new Date().toISOString(),
      };
      setVerifiedData(checkInData);
      setShowOpeningBalance(true);
    } catch (err) {
      setShiftError("Failed to check in with backend. Please try again.");
    }
  };

  const handleOpeningBalanceConfirm = async (
    amount: number,
    note: string,
    denominations?: any
  ) => {
    const outletOpeningCash = Number(verifiedData?.openingCash ?? verifiedData?.outlet?.cashAmount ?? verifiedData?.outlet?.accountBalance ?? 0);
    if (amount > outletOpeningCash) {
      toast.error(`Opening balance cannot exceed the outlet cash amount (${outletOpeningCash.toFixed(2)})`);
      return;
    }

    // Store complete check-in data with opening balance
    const completeCheckInData = {
      ...verifiedData,
      openingBalance: {
        amount,
        note,
        denominations,
        timestamp: new Date().toISOString(),
      },
    };
    sessionStorage.setItem('posCheckIn', JSON.stringify(completeCheckInData));
    sessionStorage.setItem(
      'posOpeningBalance',
      JSON.stringify({
        amount,
        note,
        timestamp: new Date().toISOString(),
      })
    );
    const clientProfileRaw = localStorage.getItem("clientBusinessProfile");
    let currentUserId: number | null = null;
    if (clientProfileRaw) {
      try {
        const profile = JSON.parse(clientProfileRaw);
        currentUserId = profile?.userId ?? profile?.UserId ?? null;
      } catch {
        currentUserId = null;
      }
    }

    const entity = {
      counterId: Number(verifiedData?.counter?.id),
      userId: currentUserId,
      openingCash: amount,
      startTime: new Date(),
      outletSessionId: verifiedData?.outletSession?.id,
    };

    try {
      if (!verifiedData?.counter?.isActive) {
        await posApi.changeCounterActiveStatus({ counterId: Number(verifiedData?.counter?.id), isActive: true });
      }
      const checkInRes = await posApi.checkInCounter(entity);
      const sessionId = unwrapResponse<number>(checkInRes);
      const counterSessionRes = await posApi.getCounterSessionInfo(sessionId);
      const counterSessionInfo = unwrapResponse<any>(counterSessionRes);

      const counterCheckedInInfo = {
        outletName: verifiedData?.outlet?.name,
        counterName: verifiedData?.counter?.name,
      };
      localStorage.setItem("counterCheckedInInfo", JSON.stringify(counterCheckedInInfo));
      localStorage.setItem("counterSession", JSON.stringify(counterSessionInfo));
      localStorage.setItem("counter", JSON.stringify(verifiedData?.counter));
      localStorage.setItem("outlet", JSON.stringify(verifiedData?.outlet));
      localStorage.setItem("outletSession", JSON.stringify(verifiedData?.outletSession));
      const checkedInWarehouseId =
        Number(
          verifiedData?.outletDetails?.warehouse?.id ??
            verifiedData?.outlet?.warehouse?.id ??
            verifiedData?.outlet?.warehouseId ??
            0
        ) || 0;
      if (checkedInWarehouseId) {
        localStorage.setItem("warehouseId", String(checkedInWarehouseId));
      }
    } catch {
      // If check-in fails, keep user on the page to retry.
      setShiftError("Failed to open counter session. Please try again.");
      return;
    }

    appendDrawerEvent({
      type: 'opening_balance',
      amount,
      reason: 'Shift opening balance',
      note,
      user: verifiedData?.user?.name || 'Cashier',
      role: 'Cashier',
    });

    // Navigate to POS checkout
    navigate('/pos-checkout');
  };

  const handleOpeningBalanceCancel = () => {
    setShowOpeningBalance(false);
    setVerifiedData(null);
  };

  const getStatusDotColor = (status: string) => {
    switch (status) {
      case 'available':
        return 'bg-green-500';
      case 'occupied':
        return 'bg-red-500';
      case 'maintenance':
        return 'bg-amber-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'available':
        return 'Available';
      case 'occupied':
        return 'Occupied';
      case 'maintenance':
        return 'Maintenance';
      default:
        return '';
    }
  };

  const isOutletSessionActive =
    outletSessionInfo && outletSessionInfo?.endTime == null;

  const selectedCounterNeedsVerification =
    !!selectedCounterData?.lastSessionInfo &&
    !selectedCounterData?.hasActiveSession &&
    !selectedCounterData?.lastSessionInfo?.sessionStatus?.name?.toLowerCase().includes('verif') &&
    !verifiedCounters.has(selectedCounterData?.id);

  const isCounterActiveAndVerified =
    !!selectedCounterData &&
    selectedCounterData?.isActive === true &&
    !selectedCounterData?.isActiveForCurrentUser &&
    (
      selectedCounterData?.lastSessionInfo?.sessionStatus?.name?.toLowerCase().includes('verif') ||
      verifiedCounters.has(selectedCounterData?.id)
    );

  const canStartShift =
    !!selectedOutlet &&
    !!selectedCounterData &&
    isOutletSessionActive &&
    !selectedCounterNeedsVerification &&
    !isCounterActiveAndVerified &&
    (
      selectedCounterData?.isActiveForCurrentUser ||
      !(selectedCounterData?.hasActiveSession && selectedCounterData?.isActive)
    );

  // Outlet checkout is allowed when no counter is currently active AND
  // all counters with unverified ended sessions (no sessionStatus) have been verified
  const hasAnyActiveCounterSession = availableCounters.some(c => c.hasActiveSession);
  const allSessionsVerified =
    !hasAnyActiveCounterSession &&
    availableCounters
      .filter(c => c.lastSessionInfo && !c.hasActiveSession && !c.lastSessionInfo?.sessionStatus?.name?.toLowerCase().includes('verif'))
      .every(c => verifiedCounters.has(c.id));

  const handleVerifySession = (counter: Counter) => {
    if (!counter.lastSessionInfo?.id) return;
    setVerifyModalCounter(counter);
  };

  const handleVerifySessionConfirm = async (transferedAmount: number) => {
    const counter = verifyModalCounter;
    if (!counter) return;
    const counterId = Number(counter.id);
    const session = counter.lastSessionInfo;

    setVerifyingCounters(prev => ({ ...prev, [counter.id]: true }));
    try {
      const payload = {
        counterId,
        counterSessionId: session.id,
        transferedAmount,
        cashIn: session.cashIn ?? 0,
        cashOut: session.cashOut ?? 0,
        salesReturnAmount: session.totalSalesReturnAmount ?? session.salesReturnAmount ?? 0,
        salesAmount: session.totalSalesAmount ?? session.salesAmount ?? 0,
        cashShortOrExcess: session.cashShortOrExcess ?? 0,
        receivedBankAmount: session.totalReceivedBankAmount ?? session.receivedBankAmount ?? 0,
        salesRefundAmount: session.totalSalesRefundAmount ?? session.salesRefundAmount ?? 0,
        adjustedCreditAmount: session.totalAdjustedCreditAmount ?? session.adjustedCreditAmount ?? 0,
      };
      await posApi.verifyCounterSession(payload);
      toast.success(`Session verified for ${counter.name}.`);
      setVerifyModalCounter(null);
      setVerifiedCounters(prev => new Set([...prev, counter.id]));
      await Promise.all([
        loadCounters(selectedOutlet!),
        loadOutletSession(selectedOutlet!),
      ]);
    } catch {
      setVerifyingCounters(prev => ({ ...prev, [counter.id]: false }));
      toast.error("Failed to verify session. Please try again.");
      throw new Error("Failed to verify session. Please try again.");
    } finally {
      setVerifyingCounters(prev => ({ ...prev, [counter.id]: false }));
    }
  };

  const handleActivateCounter = (counter: Counter) => {
    setSelectedCounter(counter.id);
    setSelectedCounterData(counter);
  };

  const handleOutletCheckout = async () => {
    if (!selectedOutlet || !outletSessionInfo) return;
    setOutletActionError(null);

    const outletId = Number(selectedOutlet);
    if (Number.isNaN(outletId)) {
      setOutletActionError("Invalid outlet selected.");
      return;
    }

    try {
      const anyActiveRes = await posApi.anyCounterIsActive(outletId);
      const anyActive = unwrapResponse<any>(anyActiveRes);
      if (anyActive === true) {
        const latestCounters = await loadCounters(selectedOutlet, outletSessionInfo);
        const hasActiveInUi = latestCounters.some((counter) => counter.isActive);
        if (hasActiveInUi) {
          setOutletActionError("To check out, all counters must be inactive.");
          return;
        }
      }
    } catch {
      setOutletActionError("Failed to verify counters. Please try again.");
      return;
    }

    // Fetch latest realtime session data before opening modal
    const freshSession = await loadOutletSession(selectedOutlet);
    setCheckoutModalSession(freshSession);
    setShowOutletCheckoutModal(true);
  };

  const handleOutletCheckoutConfirm = async (countedAmount: number) => {
    if (!selectedOutlet || !outletSessionInfo) return;
    const outletId = Number(selectedOutlet);
    const userId = getCurrentUserId();
    const session = outletSessionInfo ?? {};

    const sessionClosingCash = Number(session?.closingCash ?? session?.openingCash ?? 0);

    const payload = {
      id: session?.id ?? null,
      outletId,
      outletSessionId: session?.id ?? null,
      userId,
      openingCash: Number(session?.openingCash ?? 0),
      totalReceivedCash: Number(session?.totalReceivedCash ?? 0),
      totalSalesReturnAmount: Number(session?.totalSalesReturnAmount ?? 0),
      totalReceivedBankAmount: Number(session?.totalReceivedBankAmount ?? 0),
      totalSalesRefundAmount: Number(session?.totalSalesRefundAmount ?? 0),
      totalAdjustedCreditAmount: Number(session?.totalAdjustedCreditAmount ?? 0),
      totalSalesAmount: Number(session?.totalSalesAmount ?? 0),
      startTime: session?.startTime ? new Date(session.startTime) : new Date(),
      endTime: new Date(),
      closingCash: sessionClosingCash,
      cashShortOrExcess: countedAmount - sessionClosingCash,
    };

    await posApi.checkOutOutlet(payload);

    setShowOutletCheckoutModal(false);
    toast.success("Outlet checked out successfully.");
    localStorage.removeItem("warehouseId");
    localStorage.removeItem("pendingCheckedInOutlet");
    setCheckedInOutletId(null);
    setSelectedOutlet('');
    selectedOutletRef.current = '';
    setSelectedCounter('');
    setSelectedCounterData(null);
    setAvailableCounters([]);
    setOutletSessionInfo(null);
    setVerifiedCounters(new Set());
    hasAutoSelectedRef.current = false;
    await loadOutlets();
  };


  return (
    <div className="min-h-screen relative p-8">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: 'url(https://static.readdy.ai/image/82828e1ff69cacd1e8315694d834201a/02729a0bd7704f1310dd99c4c0c28996.png)' }}
      >
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">POS Check-In</h1>
            <p className="text-gray-600 text-sm">Select your outlet and counter to begin your shift</p>
            {apiError && (
              <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 inline-block">
                {apiError}
              </p>
            )}
          </div>
          <button
            onClick={() => signOut()}
            disabled={checkedInOutletId !== null}
            title={checkedInOutletId !== null ? 'Check out the outlet before logging out' : undefined}
            className="px-4 py-2 rounded-lg bg-white border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Logout
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Outlet & Counter Selection */}
          <div className="lg:col-span-2 space-y-6">
            {/* Select Outlet */}
            <div className="bg-white/95 backdrop-blur-sm rounded-lg p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <i className="ri-store-3-line text-xl text-gray-700"></i>
                <h2 className="text-lg font-semibold text-gray-900">Select Outlet</h2>
              </div>
              <div className="space-y-3">
                {loadingOutlets && (
                  <div className="text-sm text-gray-500">Loading outlets...</div>
                )}
                {!loadingOutlets && outlets.length === 0 && (
                  <div className="text-sm text-gray-500">No outlets found.</div>
                )}
                {outlets.map(outlet => {
                  const isLockedOut = checkedInOutletId !== null && checkedInOutletId !== outlet.id;
                  const isDisabled = isLockedOut || !!outlet.isOccupiedByOtherUser;
                  const disabledTitle = isLockedOut
                    ? 'You are already checked in to another outlet'
                    : outlet.isOccupiedByOtherUser
                    ? 'Another user is active in this outlet'
                    : undefined;
                  return (
                  <button
                    key={outlet.id}
                    onClick={() => !isDisabled && handleOutletSelect(outlet.id)}
                    disabled={isDisabled}
                    title={disabledTitle}
                    className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                      isDisabled
                        ? 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed'
                        : selectedOutlet === outlet.id
                        ? 'border-blue-500 bg-blue-50 cursor-pointer'
                        : 'border-gray-200 bg-white hover:border-gray-300 cursor-pointer'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <i className="ri-building-line text-lg text-gray-700"></i>
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-semibold text-gray-900 text-sm truncate">{outlet.name}</h3>
                          <p className="text-xs text-gray-500 truncate">{outlet.address}</p>
                          {outlet.isOccupiedByOtherUser && (
                            <p className="text-[10px] text-red-500">Another user is active</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <div className="flex gap-6 text-[11px] w-80">
                          <div className="flex-1 space-y-1 min-w-0">
                            <div className="min-w-0">
                              <span className="text-gray-400">Last User: </span>
                              <span className="font-medium text-blue-600 truncate">{outlet.lastSessionInfo?.user?.email || '—'}</span>
                            </div>
                            <div>
                              <span className="text-gray-400">Last Closing Cash: </span>
                              <span className="font-medium text-gray-700">{formatMoney(outlet.lastSessionInfo?.closingCash)}</span>
                            </div>
                          </div>
                          <div className="flex-1 space-y-1">
                            <div>
                              <span className="text-gray-400">Cash Amount: </span>
                              <span className="font-medium text-gray-700">{formatMoney(outlet.accountBalance)}</span>
                            </div>
                            <div>
                              <span className="text-gray-400">Cash on Hand: </span>
                              <span className="font-medium text-gray-700">{formatMoney(outlet.lastSessionInfo?.cashOnHand ?? outlet.lastSessionInfo?.closingCash)}</span>
                            </div>
                          </div>
                        </div>
                        {selectedOutlet === outlet.id && !isDisabled && (
                          <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                            <i className="ri-check-line text-sm text-white"></i>
                          </div>
                        )}
                        {isDisabled && (
                          <i className="ri-lock-line text-gray-300 text-base flex-shrink-0"></i>
                        )}
                      </div>
                    </div>
                  </button>
                  );
                })}
              </div>
            </div>

            {/* Hint when outlet is checked in but no counter selected */}
            {checkedInOutletId && !selectedCounter && selectedOutlet && availableCounters.length > 0 && !loadingCounters && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                <i className="ri-cursor-line text-blue-500 text-lg mt-0.5 flex-shrink-0"></i>
                <div>
                  <p className="text-sm font-medium text-blue-900">Select a counter to start your shift</p>
                  <p className="text-xs text-blue-700 mt-0.5">Click on a counter card below to select it, then start or resume your session.</p>
                </div>
              </div>
            )}

            {/* Select Counter - Only show when outlet is selected */}
            {selectedOutlet && (
              <div className="bg-white/95 backdrop-blur-sm rounded-lg p-6 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                  <i className="ri-computer-line text-xl text-gray-700"></i>
                  <h2 className="text-lg font-semibold text-gray-900">Select Counter</h2>
                </div>
                {loadingOutletSession && (
                  <div className="mb-3 text-xs text-gray-500">
                    Loading outlet session info...
                  </div>
                )}
                {outletSessionInfo && (
                  <div className="mb-3 flex items-center justify-between">
                    <span className="text-xs text-gray-600">
                      Last Session: {outletSessionInfo?.sessionCode || "N/A"}
                    </span>
                    {isOutletSessionActive && (
                      <button
                        type="button"
                        onClick={handleOutletCheckout}
                        disabled={!allSessionsVerified}
                        title={!allSessionsVerified ? 'Verify all counter sessions before checking out' : undefined}
                        className={`px-3 py-1.5 rounded-lg border text-xs transition-all ${
                          allSessionsVerified
                            ? 'border-gray-200 text-gray-700 hover:bg-gray-50 cursor-pointer'
                            : 'border-gray-100 text-gray-400 cursor-not-allowed opacity-60'
                        }`}
                      >
                        Outlet Check Out
                      </button>
                    )}
                  </div>
                )}
                {outletSessionInfo && !isOutletSessionActive && (
                  <div className="mb-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                    Outlet is checked out. Selecting the outlet will check it in.
                  </div>
                )}
                {checkingInOutlet && (
                  <div className="mb-3 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded px-3 py-2">
                    Checking in outlet...
                  </div>
                )}
                {outletActionError && (
                  <div className="mb-3 text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
                    {outletActionError}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  {loadingCounters && (
                    <div className="col-span-3 text-sm text-gray-500">Loading counters...</div>
                  )}
                  {availableCounters.map(counter => (
                    <div
                      key={counter.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        if (counter.status !== 'occupied' || counter.isActiveForCurrentUser) {
                          setSelectedCounter(counter.id);
                          setSelectedCounterData(counter);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          if (counter.status !== 'occupied' || counter.isActiveForCurrentUser) {
                            setSelectedCounter(counter.id);
                            setSelectedCounterData(counter);
                          }
                        }
                      }}
                      className={`relative p-4 rounded-lg border-2 transition-all ${
                        selectedCounter === counter.id
                          ? 'border-blue-500 bg-blue-50'
                          : counter.status === 'occupied' && !counter.isActiveForCurrentUser
                          ? 'border-red-200 bg-red-50 cursor-not-allowed opacity-60'
                          : counter.status === 'occupied' && counter.isActiveForCurrentUser
                          ? 'border-blue-200 bg-blue-50 cursor-pointer'
                          : !counter.isActive
                          ? 'border-gray-200 bg-white hover:border-gray-300 cursor-pointer'
                          : 'border-gray-200 bg-white hover:border-gray-300 cursor-pointer'
                      }`}
                    >
                      {selectedCounter === counter.id && (
                        <div className="absolute top-2 right-2 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                          <i className="ri-check-line text-xs text-white"></i>
                        </div>
                      )}

                      <div className="flex flex-col gap-3">
                        <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                              <i className="ri-computer-line text-lg text-gray-700"></i>
                            </div>
                            <div className="text-left">
                              <p className="font-semibold text-gray-900 text-sm">{counter.name}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <span
                                  className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                    counter.isActive
                                      ? 'bg-green-100 text-green-700'
                                      : 'bg-red-100 text-red-700'
                                  }`}
                                >
                                  {counter.isActive ? 'Active' : 'Inactive'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="text-xs text-gray-600 space-y-1">
                          <div className="flex items-center justify-between">
                            <span>Last Closing Time:</span>
                            <span className="font-medium text-gray-800">
                              {counter.lastSessionInfo?.endTime
                                ? new Date(counter.lastSessionInfo.endTime).toLocaleString()
                                : '—'}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span>Last Closing Cash:</span>
                            <span className="font-medium text-gray-800">
                              {formatMoney(counter.lastSessionInfo?.closingCash)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span>Last User:</span>
                            <span className="font-medium text-blue-600">
                              {counter.userEmail || '—'}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span>Cash short or excess:</span>
                            <span className="font-medium text-gray-800">
                              {formatMoney(counter.lastSessionInfo?.cashShort ?? counter.lastSessionInfo?.cashShortOrExcess)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span>Transferred Amount:</span>
                            <span className="font-medium text-gray-800">
                              {formatMoney(counter.lastSessionInfo?.transferedAmount)}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center justify-between">
                          {counter.isActive ? (
                            <>
                              {counter.lastSessionInfo?.sessionStatus?.name && (
                                <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-[10px] font-semibold">
                                  {counter.lastSessionInfo.sessionStatus.name}
                                </span>
                              )}
                              {counter.isActiveForCurrentUser && (
                                <span className="px-2 py-0.5 rounded-full bg-blue-600 text-white text-[10px] font-semibold">
                                  Resume Session
                                </span>
                              )}
                              <div className="ml-auto flex items-center gap-2">
                                {counter.lastSessionInfo && !counter.hasActiveSession && !verifiedCounters.has(counter.id) && !counter.lastSessionInfo?.sessionStatus?.name?.toLowerCase().includes('verif') && (
                                  verifyingCounters[counter.id] ? (
                                    <span className="px-3 py-1 rounded-full text-[10px] font-semibold border border-gray-200 text-gray-400">
                                      Verifying...
                                    </span>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleVerifySession(counter);
                                      }}
                                      className="px-3 py-1 rounded-full text-[10px] font-semibold border border-amber-400 text-amber-700 hover:bg-amber-50 cursor-pointer"
                                    >
                                      Verify Session
                                    </button>
                                  )
                                )}
                              </div>
                            </>
                          ) : (
                            <div className="ml-auto flex items-center gap-2">
                              {(
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleActivateCounter(counter);
                                  }}
                                  className="px-3 py-1 rounded-full text-[10px] font-semibold border border-green-400 text-green-700 hover:bg-green-50 cursor-pointer"
                                >
                                  Activate
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          {/* Right Column - Start Shift - Show when any counter is selected */}
          {selectedCounter && !!selectedCounterData && (
            <div className="lg:col-span-1">
              <div className="bg-white/95 backdrop-blur-sm rounded-lg p-6 shadow-sm sticky top-8 outline-none">
                <div className="flex items-center gap-2 mb-4">
                  <i className={`ri-${selectedCounterData?.isActiveForCurrentUser ? 'refresh-line' : 'play-circle-line'} text-xl text-gray-700`}></i>
                  <h2 className="text-lg font-semibold text-gray-900">
                    {selectedCounterData?.isActiveForCurrentUser ? 'Resume Session' : 'Start Shift'}
                  </h2>
                </div>

                <div className="mb-6 space-y-3 text-sm text-gray-600">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-gray-500">Outlet</span>
                    <span className="font-medium text-gray-900">
                      {outlets.find(o => o.id === selectedOutlet)?.name || ''}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-gray-500">Counter</span>
                    <span className="font-medium text-gray-900">
                      {selectedCounterData?.name || ''}
                    </span>
                  </div>
                </div>

                {shiftError && (
                  <div className="mb-4 text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
                    {shiftError}
                  </div>
                )}

                {/* Continue Button */}
                <button
                  onClick={handleStartShift}
                  disabled={!canStartShift}
                  title={selectedCounterNeedsVerification ? 'Please verify the previous session before activating this counter' : undefined}
                  className={`w-full py-3 rounded-lg font-semibold text-white transition-all flex items-center justify-center gap-2 whitespace-nowrap ${
                    !canStartShift
                      ? 'bg-gray-300 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700 cursor-pointer'
                  }`}
                >
                  <i className={`ri-${selectedCounterData?.isActiveForCurrentUser ? 'refresh-line' : 'checkbox-circle-line'} text-lg`}></i>
                  {selectedCounterData?.isActiveForCurrentUser ? 'Resume Session' : 'Activate Counter & Set Opening Balance'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {showOpeningBalance && verifiedData && (
        <OpeningBalanceModal
          onConfirm={handleOpeningBalanceConfirm}
          onCancel={handleOpeningBalanceCancel}
          cashierName={user?.profile?.name || verifiedData.user?.name || ''}
          storeName={verifiedData.outlet?.name || ''}
          lane={verifiedData.counter?.name || ''}
          maxAmount={Number(verifiedData.openingCash ?? verifiedData.outlet?.cashAmount ?? verifiedData.outlet?.accountBalance ?? 0)}
        />
      )}

      {verifyModalCounter && (
        <VerifySessionModal
          counterName={verifyModalCounter.name}
          session={verifyModalCounter.lastSessionInfo}
          onClose={() => setVerifyModalCounter(null)}
          onConfirm={handleVerifySessionConfirm}
        />
      )}

      {showOutletCheckoutModal && (checkoutModalSession ?? outletSessionInfo) && (() => {
        const session = checkoutModalSession ?? outletSessionInfo ?? {};
        const outletOpeningCash = Number(session?.openingCash ?? 0);
        const sales = Number(session?.totalSalesAmount ?? 0);
        const cashIn = Number(session?.totalCashIn ?? session?.cashIn ?? 0);
        const cashOut = Number(session?.totalCashOut ?? session?.cashOut ?? 0);
        const bankMobile = Number(session?.totalReceivedBankAmount ?? 0);
        const salesReturn = Number(session?.totalSalesReturnAmount ?? 0);
        const totalCounterOpeningCash = Number(session?.totalTransferedAmount ?? session?.totalCounterOpeningCash ?? 0);
        const remainingCash = outletOpeningCash - totalCounterOpeningCash;
        const cashAmount = Number(session?.totalReceivedCash ?? 0);
        const refund = Number(session?.totalSalesRefundAmount ?? 0);
        const creditApplied = Number(session?.totalAdjustedCreditAmount ?? 0);
        const closingCash = outletOpeningCash + sales + cashIn - cashOut - bankMobile - salesReturn;
        const outletName = outlets.find(o => o.id === selectedOutlet)?.name ?? 'Outlet';

        return (
          <OutletCheckoutModal
            onClose={() => setShowOutletCheckoutModal(false)}
            onConfirm={async (counted) => {
              try {
                await handleOutletCheckoutConfirm(counted);
              } catch {
                toast.error("Failed to check out outlet. Please try again.");
                throw new Error("Failed to check out outlet. Please try again.");
              }
            }}
            outletName={outletName}
            outletOpeningCash={outletOpeningCash}
            remainingCash={remainingCash}
            totalCounterOpeningCash={totalCounterOpeningCash}
            sales={sales}
            cashIn={cashIn}
            cashOut={cashOut}
            cashAmount={cashAmount}
            bankMobile={bankMobile}
            salesReturn={salesReturn}
            refund={refund}
            creditApplied={creditApplied}
            closingCash={closingCash}
          />
        );
      })()}
    </div>
  );
}



